import {
  contextualGet,
  contextualPut,
  makeBasicQueryKeys,
  MutationFunction,
  QueryFunction,
  QueryKey,
  queryOptions,
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from '@eigtech/ui-shared-api'
import { useCheckIfAccessTokenSet } from '@eigtech/ui-shared-auth'
import {
  GetGlobalPreferencesResponse,
  GetUserPreferencesResponse,
  PutGlobalPreferencesBodyRequest,
  PutUserPreferenceBodyRequest,
  PutUserPreferencesBodyRequest,
} from '@eigtech/user-preferences-types'
import { defaults, pick } from 'lodash-es'

export const preferencesBasePath = 'user-preferences'

export const preferencesQueryKeys = {
  ...pick(makeBasicQueryKeys('preferences'), ['all']),
  user: () => [...preferencesQueryKeys.all(), 'user'],
  global: () => [...preferencesQueryKeys.all(), 'global'],
}

export function makeUserPreferencesHooks<
  UserPreferences extends Record<string, unknown>,
  GlobalPreferences extends Record<string, unknown>,
  UserPreferencesKey extends keyof UserPreferences = keyof UserPreferences,
  GlobalPreferencesKey extends keyof GlobalPreferences = keyof GlobalPreferences,
>({
  defaultGlobalPreferences,
  defaultUserPreferences,
  globalKeys,
  preferencesUi,
  userKeys,
}: {
  defaultGlobalPreferences: GlobalPreferences
  defaultUserPreferences: UserPreferences
  globalKeys: GlobalPreferencesKey[]
  preferencesUi: string
  userKeys: UserPreferencesKey[]
}) {
  /** #region User Preferences (keys) */

  const functions = Object.fromEntries(
    userKeys.map(
      (key): [UserPreferencesKey, UserPreferenceConfig<UserPreferences, UserPreferencesKey>] => {
        return [key, makeUserPreferenceConfig(key, defaultUserPreferences, preferencesUi)]
      }
    )
  ) as unknown as { [K in UserPreferencesKey]: UserPreferenceConfig<UserPreferences, K> }
  /** #endregion */

  /** #region User Preferences (all) */
  function mergeUserPreferences(
    oldPreferences: UserPreferences,
    newPreferences: Partial<UserPreferences>
  ): UserPreferences {
    return {
      ...oldPreferences,
      ...newPreferences,
    }
  }

  async function getUserPreferences() {
    const response = await contextualGet<GetUserPreferencesResponse<UserPreferences>>(
      `${preferencesBasePath}/`,
      {
        searchParams: {
          ui: preferencesUi,
        },
      }
    )

    return mergeUserPreferences(
      defaultUserPreferences as UserPreferences,
      response.preferences as Partial<UserPreferences>
    )
  }

  function useGetUserPreferences() {
    const accessTokenSet = useCheckIfAccessTokenSet()
    return useQuery({
      queryKey: preferencesQueryKeys.user(),
      queryFn: getUserPreferences,
      enabled: accessTokenSet,
    })
  }

  async function putUserPreferences(payload: UserPreferences) {
    return contextualPut<UserPreferences, PutUserPreferencesBodyRequest<UserPreferences>>(
      `${preferencesBasePath}/`,
      {
        ui: preferencesUi,
        preferences: payload,
      }
    )
  }

  function usePutUserPreferences() {
    return usePutPreferences({
      preferenceKey: 'user',
      queryFunction: getUserPreferences,
      mergeFunction: mergeUserPreferences,
      mutationFunction: putUserPreferences,
    })
  }
  /** #endregion */

  /** #region Global Preferences */

  async function getGlobalPreferences(): Promise<GlobalPreferences> {
    const { preferences } = await contextualGet<GetGlobalPreferencesResponse<GlobalPreferences>>(
      `${preferencesBasePath}/global`,
      {
        searchParams: { ui: preferencesUi },
      }
    )

    // set default preferences in case they are not set
    // only include preferences in type (i.e. remove unused preference keys)
    return defaults(pick(preferences, globalKeys), defaultGlobalPreferences as GlobalPreferences)
  }

  function useGetGlobalPreferences() {
    const accessTokenSet = useCheckIfAccessTokenSet()
    return useQuery({
      queryKey: preferencesQueryKeys.global(),
      queryFn: getGlobalPreferences,
      enabled: accessTokenSet,
    })
  }

  function useInvalidateGlobalPreferences() {
    const queryClient = useQueryClient()

    return function invalidateUserGroups() {
      queryClient.invalidateQueries({ queryKey: preferencesQueryKeys.global() })
    }
  }

  async function putGlobalPreferences(payload: GlobalPreferences) {
    return contextualPut<GlobalPreferences, PutGlobalPreferencesBodyRequest<GlobalPreferences>>(
      `${preferencesBasePath}/global`,
      { ui: preferencesUi, preferences: payload }
    )
  }

  function mergeGlobalPreferences(
    oldPreferences: GlobalPreferences,
    newPreferences: Partial<GlobalPreferences>
  ): GlobalPreferences {
    return {
      ...oldPreferences,
      ...newPreferences,
    }
  }

  function usePutGlobalPreferences() {
    return usePutPreferences({
      preferenceKey: 'global',
      queryFunction: getGlobalPreferences,
      mergeFunction: mergeGlobalPreferences,
      mutationFunction: putGlobalPreferences,
    })
  }
  /** #endregion */

  /** #region Helpers */
  type PreferencesType<PreferenceKey extends 'user' | 'global'> = PreferenceKey extends 'global'
    ? GlobalPreferences
    : UserPreferences

  type UsePutPreferencesProps<PreferenceKey extends 'user' | 'global'> = {
    preferenceKey: PreferenceKey
    queryFunction: QueryFunction<PreferencesType<PreferenceKey>>
    mergeFunction: (
      oldPreferences: PreferencesType<PreferenceKey>,
      newPreferences: Partial<PreferencesType<PreferenceKey>>
    ) => PreferencesType<PreferenceKey>
    mutationFunction: MutationFunction<
      PreferencesType<PreferenceKey>,
      PreferencesType<PreferenceKey>
    >
  }

  function usePutPreferences<PreferenceKey extends 'user' | 'global'>({
    preferenceKey,
    queryFunction,
    mergeFunction,
    mutationFunction,
  }: UsePutPreferencesProps<PreferenceKey>) {
    const queryKey =
      preferenceKey === 'global' ? preferencesQueryKeys.global() : preferencesQueryKeys.user()
    const queryClient = useQueryClient()

    return useMutation({
      mutationFn: async (preferences: Partial<PreferencesType<PreferenceKey>>) => {
        const wrappedMergeFunction = async () => {
          const oldPreferences = await queryClient.ensureQueryData<PreferencesType<PreferenceKey>>({
            queryKey,
            queryFn: queryFunction,
          })

          return mergeFunction(oldPreferences, preferences)
        }
        const newPreferences = await wrappedMergeFunction()
        return mutationFunction(newPreferences, {
          client: queryClient,
          meta: {},
          mutationKey: queryKey,
        })
      },
      async onMutate(preferences) {
        await queryClient.cancelQueries({ queryKey })

        const previousPreferences =
          queryClient.getQueryData<PreferencesType<PreferenceKey>>(queryKey)

        if (previousPreferences) {
          const newPreferences = await mergeFunction(previousPreferences, preferences)
          queryClient.setQueryData(queryKey, newPreferences)
        }

        return { previousPreferences }
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onError(__error, __newSettings, context: any) {
        if (context?.previousPreferences) {
          queryClient.setQueryData(queryKey, context.previousPreferences)
        }

        queryClient.invalidateQueries({ queryKey })
      },
      onSuccess(data) {
        queryClient.setQueryData(queryKey, data)
      },
    })
  }
  /** #endregion */

  return {
    ...functions,
    useGetUserPreferences,
    usePutUserPreferences,
    useGetGlobalPreferences,
    useInvalidateGlobalPreferences,
    usePutGlobalPreferences,
  }
}

function makeUserPreferenceConfig<
  UserPreferences extends Record<string, unknown>,
  UserPreferencesKey extends keyof UserPreferences = keyof UserPreferences,
>(key: UserPreferencesKey, defaultUserPreferences: UserPreferences, preferencesUi: string) {
  const localStorageKey = `preferences-${String(key)}`

  async function getUserPreference() {
    const response = await contextualGet<
      GetUserPreferencesResponse<UserPreferences, UserPreferencesKey>
    >(`${preferencesBasePath}/`, {
      searchParams: {
        ui: preferencesUi,
        key: key.toString(),
      },
    })

    if (response.preferences === undefined) {
      return defaultUserPreferences[key as keyof typeof defaultUserPreferences]
    }

    return response.preferences
  }

  async function putUserPreference({
    key,
    preference,
  }: {
    key: UserPreferencesKey
    preference: UserPreferences[UserPreferencesKey]
  }) {
    return contextualPut<
      UserPreferences,
      PutUserPreferenceBodyRequest<UserPreferences, UserPreferencesKey>
    >(`${preferencesBasePath}/`, {
      ui: preferencesUi,
      key,
      preference,
    })
  }

  const queryKey: QueryKey = [...preferencesQueryKeys.user(), key]
  const getQueryOptions = (enabled: boolean) =>
    queryOptions({
      queryKey: queryKey,
      queryFn: () => getUserPreference(),
      enabled,
      initialData() {
        const localPreference = localStorage.getItem(localStorageKey)

        if (localPreference) {
          try {
            return JSON.parse(localPreference) as
              | UserPreferences[keyof UserPreferences]
              | ((UserPreferencesKey extends keyof UserPreferences
                  ? UserPreferences[UserPreferencesKey]
                  : UserPreferences) & {})
          } catch {
            //
          }
        }
      },
    })

  function useGetPreference() {
    const accessTokenSet = useCheckIfAccessTokenSet()

    return useQuery(getQueryOptions(accessTokenSet)) as UseQueryResult<
      UserPreferences[UserPreferencesKey]
    >
  }

  function useSuspenseGetPreference() {
    const accessTokenSet = useCheckIfAccessTokenSet()
    return useSuspenseQuery(getQueryOptions(accessTokenSet)) as UseSuspenseQueryResult<
      UserPreferences[UserPreferencesKey]
    >
  }

  function useInvalidatePreference() {
    const queryClient = useQueryClient()

    return function invalidateQuery() {
      queryClient.invalidateQueries({ queryKey })
    }
  }

  function usePutPreference() {
    const queryClient = useQueryClient()

    type Request = UserPreferences[UserPreferencesKey]

    return useMutation({
      mutationFn: (preference: Request) => putUserPreference({ key, preference }),
      async onMutate(preference) {
        await queryClient.cancelQueries({ queryKey: preferencesQueryKeys.user() })
        const previousPreferences = queryClient.getQueryData<UserPreferences>(
          preferencesQueryKeys.user()
        )
        queryClient.setQueryData(preferencesQueryKeys.user(), {
          ...previousPreferences,
          [key]: preference,
        })

        await queryClient.cancelQueries({ queryKey })

        const previousPreference = queryClient.getQueryData(queryKey)

        queryClient.setQueryData(queryKey, preference)

        return { previousPreference }
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onError(__error, __newSettings, context: any) {
        if (context?.previousPreference) {
          queryClient.setQueryData(queryKey, context.previousPreference)
        }

        queryClient.invalidateQueries({ queryKey })
      },
      onSuccess(__data, request) {
        localStorage.setItem(localStorageKey, JSON.stringify(request))
      },
    }) as UseMutationResult<UserPreferences, unknown, Request>
  }

  return {
    queryKey,
    getQueryOptions,
    useGetPreference,
    usePutPreference,
    useSuspenseGetPreference,
    useInvalidatePreference,
  }
}

type UserPreferenceConfig<
  UserPreferences extends Record<string, unknown>,
  UserPreferencesKey extends keyof UserPreferences = keyof UserPreferences,
> = ReturnType<typeof makeUserPreferenceConfig<UserPreferences, UserPreferencesKey>>
